import KeenSlider, { KeenSliderOptions, KeenSliderInstance } from 'keen-slider';

import { CLASS_ACTIVE, CLASS_INITIALISED } from '../tools/constants/classes';

export type CarouselOptions = KeenSliderOptions & {
  autoplay?: boolean;
  autoplaySpeed?: number;
};

const CAROUSEL_DEFAULTS: CarouselOptions = {
  autoplay: false,
  autoplaySpeed: 10,
};

// go to slide when change variant
const swatchesEL: NodeListOf<HTMLElement> = document.querySelectorAll(
  '[data-swatches] button.o-swatch'
);
const carrouselDotEL: NodeListOf<HTMLElement> = document.querySelectorAll(
  '.c-product-image-carousel__dots [data-carousel-dot]'
);
const selectSwatchesEL: NodeListOf<HTMLElement> = document.querySelectorAll(
  '[data-swatches] select.o-select'
);
const optionsSWatchesEL: NodeListOf<HTMLElement> = document.querySelectorAll(
  '[data-swatches] select.o-select option'
);

const goToSlide = (id: any, selector: any) => {
  carrouselDotEL.forEach((dot: any) => {
    let dotColor = '';
    if (selector === 'id') {
      dotColor = dot.getAttribute('data-variant-id');
    } else if (selector === 'title') {
      dotColor = dot.getAttribute('data-variant-title');
    }
    // venodr-TODO: Find a way to change index of carousel
    if (dotColor === id) {
      dot.click();
    }
  });
};

selectSwatchesEL.forEach((select: any) => {
  select.addEventListener('change', (evt: any) => {
    const select = evt.currentTarget as HTMLSelectElement;
    const selectColor = select.value;
    let optionId = '';
    optionsSWatchesEL.forEach((option: any) => {
      const optionColor = option.value;
      if (optionColor === selectColor) {
        optionId = option.getAttribute('data-option-variant-id');
      }
    });
    goToSlide(optionId, 'id');
  });
});

swatchesEL.forEach((swatch) => {
  swatch.addEventListener('click', (evt) => {
    const swatch = evt.currentTarget as HTMLElement;
    const swatchColor = swatch.getAttribute('data-value');
    goToSlide(swatchColor, 'title');
  });
});

export type KeenEventCallback = (params?: any) => void;

// Selector
const SELECTOR_DOT = '[data-carousel-dot]';
const SELECTOR_THUMBNAIL = '[data-carousel-thumbnail]';
const SELECTOR_PREV = '[data-carousel-previous]';
const SELECTOR_NEXT = '[data-carousel-next]';

const CLASS_PREV = 'is-prev-active';
const CLASS_NEXT = 'is-next-active';
const CLASS_PREV_2 = 'is-2-prev-active';
const CLASS_NEXT_2 = 'is-2-next-active';

export interface InitCarouselParams {
  container: HTMLElement;
  options: CarouselOptions;
  dotsContainer?: HTMLElement | null;
  thumbnailsContainer?: HTMLElement | null;
  navigationContainer?: HTMLElement | null;
  onSlideChangedCallback?: KeenEventCallback | null;
  variableWidth?: boolean | null;
  fade?: boolean | null;
  progressBar?: HTMLElement | null;
}

export type CarouselObject = ReturnType<typeof initCarousel>;

export const initCarousel = (params: InitCarouselParams) => {
  const {
    container,
    dotsContainer,
    thumbnailsContainer,
    navigationContainer,
    onSlideChangedCallback,
    variableWidth,
    fade,
    progressBar,
  } = params;

  const options = { ...CAROUSEL_DEFAULTS, ...params.options };

  const slides: HTMLElement[] = [];
  // This is never assigned, what the heck?
  // eslint-disable-next-line prefer-const
  let slider: KeenSliderInstance;

  container.classList.add('keen-slider');
  container.childNodes.forEach((child) => {
    if (typeof (child as HTMLElement).classList === typeof undefined) return;
    const element = child as HTMLElement;
    element.classList.add('keen-slider__slide');
    slides.push(element);
  });

  // Autoplay
  let autoplayTimeout: ReturnType<typeof setTimeout>;
  let autoplayState: boolean;
  const autoplay = (state?: boolean) => {
    const newState = Boolean(state);
    autoplayState = newState;
    if (!state) {
      clearTimeout(autoplayTimeout);
      return;
    }
    autoplayTimeout = setTimeout(() => {
      if (!autoplayState) return;
      autoplay(autoplayState);
      slider.next();
    }, (options.autoplaySpeed || 10) * 1000);
  };

  autoplay(options.autoplay);

  container.addEventListener('mouseover', () => {
    autoplay(false);
  });

  container.addEventListener('mouseout', () => {
    autoplay(options.autoplay);
  });

  // Setup dot events
  if (dotsContainer) {
    const dots: NodeListOf<HTMLElement> =
      dotsContainer.querySelectorAll(SELECTOR_DOT);
    if (!dots) throw new Error('carousel.ts: no dots container');
    dots.forEach((dot, idx) =>
      dot.addEventListener('click', () => slider.moveToIdx(idx))
    );
  }

  // Setup thumbnail events
  if (thumbnailsContainer) {
    const thumbnails: NodeListOf<HTMLElement> =
      thumbnailsContainer.querySelectorAll(SELECTOR_THUMBNAIL);
    if (!thumbnails) throw new Error('carousel.ts: no thumbnails container');
    thumbnails.forEach((thumbnail, idx) =>
      thumbnail.addEventListener('click', () => slider.moveToIdx(idx))
    );
  }

  // Setup navigation events
  if (navigationContainer) {
    const prev = navigationContainer.querySelector<HTMLElement>(SELECTOR_PREV);
    const next = navigationContainer.querySelector<HTMLElement>(SELECTOR_NEXT);

    if (!prev || !next)
      throw new Error('carousel.ts: no previous/next element');

    next.addEventListener('click', () => slider.next());
    prev.addEventListener('click', () => slider.prev());
  }

  // Update active class
  const updateClasses = (slider: KeenSliderInstance) => {
    const slideIdx = slider.track.details.rel;
    const numSlides = slider.track.details.slides.length;
    const slide = slides[slideIdx];

    let nextIndex = slideIdx + 1;
    let nextNextIndex = slideIdx + 2;
    let prevIndex = slideIdx - 1;
    let prevPrevIndex = slideIdx - 2;

    if (nextIndex >= numSlides) {
      nextIndex = 0;
      nextNextIndex = 1;
    }

    if (nextNextIndex >= numSlides) {
      nextNextIndex = 0;
    }

    if (prevIndex < 0) {
      prevIndex = numSlides - 1;
      prevPrevIndex = prevIndex - 1;
    }

    if (prevPrevIndex < 0) {
      prevPrevIndex = numSlides - 1;
    }

    const slideNext = slides[nextIndex];
    const slidePrev = slides[prevIndex];
    const slideNextNext = slides[nextNextIndex];
    const slidePrevPrev = slides[prevPrevIndex];

    // Manage slide classes
    slides.forEach((slide) =>
      slide.classList.remove(
        CLASS_ACTIVE,
        CLASS_NEXT,
        CLASS_PREV,
        CLASS_NEXT_2,
        CLASS_PREV_2
      )
    );

    if (slide) slide.classList.add(CLASS_ACTIVE);
    if (slideNext) slideNext.classList.add(CLASS_NEXT);
    if (slidePrev) slidePrev.classList.add(CLASS_PREV);

    if (slides.length >= 5) {
      slideNextNext.classList.add(CLASS_NEXT_2);
      slidePrevPrev.classList.add(CLASS_PREV_2);
    }

    // Manage dot classes
    if (dotsContainer) {
      dotsContainer.classList.add(CLASS_INITIALISED);
      const dots: NodeListOf<HTMLElement> =
        dotsContainer.querySelectorAll(SELECTOR_DOT);
      dots.forEach((dot, idx) => {
        if (idx === slideIdx) {
          dot.classList.add(CLASS_ACTIVE);
        } else {
          dot.classList.remove(CLASS_ACTIVE);
        }
      });
    }

    // Manage thumbnail classes
    if (thumbnailsContainer) {
      thumbnailsContainer.classList.add(CLASS_INITIALISED);
      const thumbnails: NodeListOf<HTMLElement> =
        thumbnailsContainer.querySelectorAll(SELECTOR_THUMBNAIL);
      thumbnails.forEach((thumbnail, idx) => {
        if (idx === slideIdx) {
          thumbnail.classList.add(CLASS_ACTIVE);
        } else {
          thumbnail.classList.remove(CLASS_ACTIVE);
        }
      });
    }
  };

  // On slide chaged
  const onSlideChanged = (slider: KeenSliderInstance) => {
    // Update classes
    updateClasses(slider);

    // Update progress bar
    if (progressBar) {
      const details = slider.track.details;
      const fraction = 100 / details.slides.length;
      const currentProgress = fraction * details.rel + fraction;
      progressBar.style.width = `${currentProgress}%`;
    }

    // Run callback function if passed
    if (onSlideChangedCallback) {
      onSlideChangedCallback();
    }
  };

  // Allows for slide movement based on variable width slides
  const variableWidthMovement = (slider: KeenSliderInstance) => {
    if (!variableWidth) return;

    const details = slider.track.details;
    const slidePercent = details.slides.length * details.progress;

    let offsetWidth = 0;
    let totalSlideWidth = 0;
    for (let i = 0; i < slides.length; i++) {
      const slideWidth = slides[i].getBoundingClientRect().width;
      totalSlideWidth += slideWidth;

      const next = i + 1;

      if (slidePercent >= next) {
        offsetWidth += slideWidth;
      } else if (slidePercent > i && slidePercent < next) {
        offsetWidth += slideWidth * (slidePercent - i);
      }
    }

    /**
     * If the slide tracks progress is more then halfway through, grab the 1st
     * quarter of slides and throw them back to the end of the que
     */
    for (let i = 0; i < slides.length; i++) {
      let slideOffset = -offsetWidth;
      const loopAround = i < Math.floor(slidePercent);
      if (loopAround) slideOffset += totalSlideWidth;
      slides[i].style.transform = `translate3d(${slideOffset.toFixed(
        2
      )}px, 0, 0)`;
    }
  };

  // Fades slides instead of transforming them
  const fadeMovement = (slider: KeenSliderInstance) => {
    const opacities = slider.track.details.slides.map((slide) => slide.portion);
    slides.forEach((element, idx) => {
      element.style.opacity = `${opacities[idx]}`;
      element.style.transform = `translateX(-${idx * 100}%)`;
    });
  };

  // On slide moved
  const onSlideMoved = (slider: KeenSliderInstance) => {
    if (variableWidth) variableWidthMovement(slider);
    if (fade) fadeMovement(slider);
  };

  // On destroyed callback
  const onDestroyed = () => {
    container.classList.remove(CLASS_INITIALISED);
    slides.forEach((slide) => {
      slide.classList.remove(CLASS_ACTIVE);
      slide.removeAttribute('style');
    });
  };

  // On mounted
  const onMounted = (slider: KeenSliderInstance) => {
    container.classList.add(CLASS_INITIALISED);
    updateClasses(slider);
  };

  const onDragStart = () => {
    autoplay(false);
  };

  // Prevent dragging on specific elements
  const preventDragElements = container.querySelectorAll(
    '[data-before-after-image-container], input[type="range"], input[type="number"]'
  );
  
  preventDragElements.forEach((element) => {
    element.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });
    element.addEventListener('mousedown', (e) => {
      e.stopPropagation();
    });
    element.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
    });
  });

  // Slider Init
  slider = new KeenSlider(container, {
    dragStarted: () => onDragStart(),
    dragEnded: () => autoplay(options.autoplay),
    slideChanged: (slider) => onSlideChanged(slider),
    created: (slider) => onMounted(slider),
    destroyed: () => onDestroyed(),
    updated: (slider) => onSlideMoved(slider),
    ...options,
  });

  return {
    slider,
    autoplay,
    slides,
  };
};
